// Copyright © 2025 The Things Industries B.V.

import { useCallback, useMemo, useState } from 'react'
import { defineMessages } from 'react-intl'
import { useSelector } from 'react-redux'

import { getDeviceId } from '@ttn-lw/lib/selectors/id'

import { selectDevicesWithLastSeen } from '@console/store/selectors/devices'

const m = defineMessages({
  allDevices: 'All devices',
  lastHour: 'Last 1 hour',
  lastHours: 'Last {hours} hours',
  lastDays: 'Last {days} days',
})

const dateOptions = [
  {
    value: '1h',
    label: m.lastHour,
  },
  {
    value: '3h',
    label: m.lastHours,
    labelValues: { hours: 3 },
  },
  {
    value: '6h',
    label: m.lastHours,
    labelValues: { hours: 6 },
  },
  {
    value: '12h',
    label: m.lastHours,
    labelValues: { hours: 12 },
  },
  {
    value: '24h',
    label: m.lastHours,
    labelValues: { hours: 24 },
  },
  {
    value: '48h',
    label: m.lastDays,
    labelValues: { days: 2 },
  },
  {
    value: '168h',
    label: m.lastDays,
    labelValues: { days: 7 },
  },
  {
    value: '720h',
    label: m.lastDays,
    labelValues: { days: 30 },
  },
  {
    value: '2160h',
    label: m.lastDays,
    labelValues: { days: 90 },
  },
]

const useMessageStorage = () => {
  const devices = useSelector(selectDevicesWithLastSeen)

  const deviceIdOptions = useMemo(
    () => [
      {
        value: 'all',
        label: m.allDevices,
      },
      ...devices.map(d => ({
        value: getDeviceId(d),
        label: d.name ?? getDeviceId(d),
      })),
    ],
    [devices],
  )

  const [selectedDeviceId, setSelectedDeviceId] = useState(deviceIdOptions[0].value)
  const [date, setDate] = useState(dateOptions[3].value)

  const handleDeviceIdChange = useCallback(value => setSelectedDeviceId(value), [])

  const handleDateChange = useCallback(value => setDate(value), [])

  const deviceName = useMemo(
    () => devices.find(d => d.ids.device_id === selectedDeviceId)?.name ?? '',
    [devices, selectedDeviceId],
  )

  return {
    handleDeviceIdChange,
    handleDateChange,
    selectedDevice: { id: selectedDeviceId, name: deviceName },
    date,
    dateOptions,
    deviceIdOptions,
  }
}

export default useMessageStorage
